import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CollegeLogo from '../components/CollegeLogo';
import Button from '../components/Button';
import { matchesCoachUid } from '../lib/coachPublicProfile';
import '../components/Layout.css';

export default function CoachPublicProfilePage() {
  const { uid, profileUrl } = useParams();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: `${coach?.name || 'Coach'} | ProZpects`, url }); } catch (e) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) { /* fallback */ }
    }
  };

  useEffect(() => {
    document.title = 'Coach Profile | ProZpects';

    fetch(`${window.location.origin}/api/coaches/public/${encodeURIComponent(profileUrl)}`)
      .then(res => {
        if (!res.ok) throw new Error('Profile not found');
        return res.json();
      })
      .then(data => {
        // The slug identifies the coach; the first segment must be their uid.
        // This is also what sunsets the legacy /coach/<user_id>/<slug> form.
        if (!matchesCoachUid(data, uid)) {
          throw new Error('Profile not found');
        }
        setCoach(data);
        const name = [data.user?.first_name, data.user?.last_name].filter(Boolean).join(' ');
        document.title = `${name || 'Coach'} | ProZpects`;
      })
      .catch(() => setError('Profile not found or not available.'))
      .finally(() => setLoading(false));
  }, [uid, profileUrl]);

  if (loading) return (
    <div className="public-profile-page">
      <div className="loading-spinner">Loading...</div>
    </div>
  );

  if (error) return (
    <div className="public-profile-page">
      <div className="public-profile-error">
        <h2>Profile Not Found</h2>
        <p>{error}</p>
      </div>
    </div>
  );

  const c = coach;
  const name = [c.user?.first_name, c.user?.last_name].filter(Boolean).join(' ');
  const location = [c.city, c.state].filter(Boolean).join(', ');

  const primaryPhoto = (() => {
    const idx = c.primary_photo_index || 1;
    if (idx === 2) return c.player_photo_2 || c.player_photo;
    if (idx === 3) return c.player_photo_3 || c.player_photo;
    return c.player_photo || c.player_photo_2 || c.player_photo_3;
  })();

  const extraPhotos = [c.player_photo, c.player_photo_2, c.player_photo_3]
    .filter(Boolean)
    .filter(p => p !== primaryPhoto);

  const hasSocial = c.youtube_handle || c.twitter_handle || c.instagram_handle ||
    c.tiktok_handle || c.snapchat_handle || c.facebook_handle || c.linkedin_handle;

  return (
    <div className="public-profile-page">

      {/* Banner */}
      <div className="pp-banner">
        <img src="/logo-wordmark.png" alt="ProZpects" className="pp-banner__logo" />
      </div>

      {/* Header */}
      <div className="pp-header-outer">
        <div className="pp-header-inner">
          <div className="pp-avatar-wrap">
            {primaryPhoto ? (
              <img src={primaryPhoto} alt={name} className="pp-avatar" />
            ) : (
              <div className="pp-avatar-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                </svg>
              </div>
            )}
          </div>
          <div className="pp-header__info">
            <h1 className="pp-header__name">{name}</h1>
            {(c.school_name || c.position_title) && (
              <div className="pp-header__school">
                {(c.school_logo || c.unitid) && (
                  <CollegeLogo
                    schoolName={c.school_name}
                    logoUrl={c.school_logo}
                    unitid={c.unitid}
                    size={28}
                    className="pp-header__school-logo"
                  />
                )}
                <span>
                  {c.position_title}{c.school_name ? ` — ${c.school_name}` : ''}
                </span>
              </div>
            )}
            <div className="pp-header__meta">
              {c.sport && <span className="modal-tag">{c.sport}</span>}
              {c.division && <span>{c.division}</span>}
              {location && <span>{location}</span>}
            </div>
          </div>
          <Button onClick={handleShare} variant="secondary" size="sm" className="pp-share-btn" title="Share profile">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            {copied ? 'Copied!' : 'Share'}
          </Button>
        </div>
      </div>

      <div className="pp-divider" />

      {/* Body */}
      <div className="pp-body-outer">
        <div className="pp-body-inner">

          {/* Left: extra photos */}
          {extraPhotos.length > 0 && (
            <aside className="pp-sidebar">
              <div className="pp-sidebar-section">
                <h3 className="pp-sidebar-section__title">Photos</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {extraPhotos.map((p, i) => (
                    <img key={i} src={p} alt="" style={{ width: '100%', borderRadius: '8px', objectFit: 'cover', maxHeight: '180px' }} />
                  ))}
                </div>
              </div>
            </aside>
          )}

          {/* Right: about + social */}
          <main className="pp-main">
            {c.personal_statement && (
              <div className="pp-section">
                <h2 className="pp-section__title">About</h2>
                <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#334155', whiteSpace: 'pre-wrap' }}>
                  {c.personal_statement}
                </p>
              </div>
            )}

            {hasSocial && (
              <div className="pp-section">
                <h2 className="pp-section__title">Social</h2>
                <div className="public-profile-social">
                  {c.youtube_handle && (
                    <a href={c.youtube_handle.startsWith('http') ? c.youtube_handle : `https://youtube.com/@${c.youtube_handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="public-profile-social__link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#FF0000"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                      YouTube: {c.youtube_handle}
                    </a>
                  )}
                  {c.tiktok_handle && (
                    <a href={`https://tiktok.com/@${c.tiktok_handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="public-profile-social__link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#000000"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
                      TikTok: {c.tiktok_handle}
                    </a>
                  )}
                  {c.instagram_handle && (
                    <a href={`https://instagram.com/${c.instagram_handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="public-profile-social__link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#E1306C"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                      Instagram: {c.instagram_handle}
                    </a>
                  )}
                  {c.twitter_handle && (
                    <a href={`https://x.com/${c.twitter_handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="public-profile-social__link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#1da1f2"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      X / Twitter: {c.twitter_handle}
                    </a>
                  )}
                  {c.snapchat_handle && (
                    <a href={`https://snapchat.com/add/${c.snapchat_handle.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="public-profile-social__link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFFC00" stroke="#000" strokeWidth="0.3"><path d="M12.206 1c.732 0 3.195.21 4.394 2.887.41.929.312 2.521.228 3.889l-.013.218c-.006.093.085.175.227.175.254 0 .57-.07.806-.157.096-.035.195-.054.294-.054.23 0 .443.086.56.228a.6.6 0 0 1 .08.547c-.11.352-.573.598-1.158.739-.077.018-.163.036-.25.054-.303.06-.68.136-.793.432-.067.176-.022.385.136.618.018.025 1.738 2.489 4.252 2.943a.348.348 0 0 1 .293.362c-.025.259-.165.48-.416.648-.614.41-1.633.65-2.902.694-.052.001-.105.003-.159.005l-.115.007c-.175.13-.138.398-.074.686.044.194.1.414.1.643 0 .348-.13.617-.388.8-.56.397-1.497.45-2.553.51-.737.042-1.572.089-2.398.326-.175.051-.348.155-.528.262-.573.342-1.222.729-2.26.729s-1.688-.387-2.262-.73c-.18-.106-.352-.21-.525-.261-.826-.237-1.66-.284-2.398-.326-1.057-.06-1.993-.113-2.553-.51-.258-.183-.388-.452-.388-.8 0-.229.057-.449.101-.643.064-.288.1-.556-.075-.686l-.115-.007c-.054-.002-.107-.004-.16-.005-1.268-.043-2.288-.284-2.9-.694-.252-.168-.392-.39-.417-.648a.348.348 0 0 1 .293-.362c2.514-.454 4.234-2.918 4.252-2.943.158-.233.203-.442.136-.618-.113-.296-.49-.372-.793-.432-.087-.018-.173-.036-.25-.054-.585-.14-1.048-.387-1.158-.739a.6.6 0 0 1 .08-.547c.117-.142.33-.228.56-.228.099 0 .198.019.294.054.236.087.552.157.806.157.142 0 .233-.082.227-.175l-.013-.218c-.084-1.368-.182-2.96.228-3.889C9.011 1.21 11.474 1 12.206 1z"/></svg>
                      Snapchat: {c.snapchat_handle}
                    </a>
                  )}
                  {c.facebook_handle && (
                    <a href={c.facebook_handle.startsWith('http') ? c.facebook_handle : `https://facebook.com/${c.facebook_handle}`} target="_blank" rel="noopener noreferrer" className="public-profile-social__link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      Facebook: {c.facebook_handle}
                    </a>
                  )}
                  {c.linkedin_handle && (
                    <a href={c.linkedin_handle.startsWith('http') ? c.linkedin_handle : `https://linkedin.com/in/${c.linkedin_handle}`} target="_blank" rel="noopener noreferrer" className="public-profile-social__link">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                      LinkedIn: {c.linkedin_handle}
                    </a>
                  )}
                </div>
              </div>
            )}

            {!c.personal_statement && !hasSocial && (
              <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0 }}>No additional info available.</p>
            )}
          </main>

        </div>
      </div>

      <div className="public-profile-footer">
        Powered by <a href="/" className="public-profile-footer__link">ProZpects</a>
      </div>
    </div>
  );
}
